import React from 'react'
import { ControlledSelect, ControlledTextInput } from 'transfix-form/components'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box } from 'transfix-ui/core'
import { ParserVersion, tfx12Config } from '../types'
import { getParserOptions, getTfx12ConfigOptions } from '../utils'
import { IParserOptionsSchema } from './ParserOptionsSchema'
import { useParserOptionsForm } from './useParserOptionsForm'

interface IParserOptionsModalProps extends Omit<IModalBaseProps, 'modalType'> {
  onClose: () => void
  /**
   * List of stupidedi config options (fetched from BE), used in parser request
   * @example ["default", "anheuser_busch_3040"]
   */
  stupidediConfigOptions: string[]
  /**
   * List of tfx12 config options (fetched from BE), used in parser request
   * @example [{1 => '4010 default'}]
   */
  tfx12ConfigOptions: tfx12Config[]
  /**
   * List of document conversion options (fetched from BE), used in parser request
   * @example ["default"]
   */
  documentConversionOptions: string[]
  /**
   * List of element conversion options (Fetched from BE), used in parser request
   * @example ["default"]
   */
  elementConversionOptions: string[]
  /**
   * Parser that has been loaded/being currently edited.
   * @example { id: 1, is_live: false, was_ever_live: false, extraction_template: 'Some jsonata query', conversion_instructions: 'Some json'}
   */
  currentParserVersion?: ParserVersion
  onStupidediConfigChange: (config: string) => void
  onTfx12ConfigReferenceChange: (config: string) => void
  onParserLibraryChange: (parserLibrary: string) => void
  onDocumentConversionOptionChange: (option: string) => void
  onElementConversionOptionChange: (option: string) => void
}

export const ParserOptionsModal = ({
  currentParserVersion,
  documentConversionOptions,
  elementConversionOptions,
  onClose,
  onDocumentConversionOptionChange,
  onElementConversionOptionChange,
  onParserLibraryChange,
  onStupidediConfigChange,
  onTfx12ConfigReferenceChange,
  open = true,
  stupidediConfigOptions,
  tfx12ConfigOptions,
  ...props
}: IParserOptionsModalProps) => {
  const methods = useParserOptionsForm()

  const { control, errors, handleSubmit } = methods

  const submitHandler = handleSubmit((formParams: IParserOptionsSchema) => {
    onStupidediConfigChange(formParams.stupidediConfig)
    onTfx12ConfigReferenceChange(formParams.tfx12ConfigReference)
    onParserLibraryChange(formParams.parserLibrary)
    onDocumentConversionOptionChange(formParams.documentConversion)
    onElementConversionOptionChange(formParams.elementConversionType)
    onClose()
  })

  const lookupTfx12ConfigName = (tfx12ConfigId?: string) => {
    if (tfx12ConfigId) {
      const result = tfx12ConfigOptions.find(config => config.id === tfx12ConfigId)
      return result?.reference
    }
    return null
  }

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: submitHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  return (
    <ModalBase
      onClose={onClose}
      dataTest='ParserOptionsModal'
      modalType={MODAL_TYPES.form}
      size={2}
      open={open}
      actions={actions}
      title='Parser Options'
      {...props}>
      <Box mb={2}>
        <ControlledTextInput
          control={control}
          name='parserLibrary'
          defaultValue={currentParserVersion?.parser_library || 'stupidedi'}
          TextInputProps={{
            label: 'Parser Library',
            required: true,
            large: true,
            validationMessage: errors?.parserLibrary?.message,
            error: !!errors?.parserLibrary?.message,
          }}
        />
      </Box>
      <Box mb={2}>
        <ControlledSelect
          control={control}
          name='stupidediConfig'
          defaultValue={currentParserVersion?.stupidedi_config}
          SelectProps={{
            label: 'Stupidedi Config',
            options: getParserOptions(stupidediConfigOptions),
            large: true,
          }}
        />
      </Box>
      <Box mb={2}>
        <ControlledSelect
          control={control}
          name='tfx12ConfigReference'
          defaultValue={
            lookupTfx12ConfigName(currentParserVersion?.edi_mapper_tfx12_definition_id) || ''
          }
          SelectProps={{
            label: 'TFX12 Config',
            options: getTfx12ConfigOptions(tfx12ConfigOptions),
            large: true,
          }}
        />
      </Box>
      <Box mb={2}>
        <ControlledSelect
          control={control}
          name='elementConversionType'
          defaultValue={currentParserVersion?.element_conversion}
          SelectProps={{
            label: 'Element Conversion',
            options: getParserOptions(elementConversionOptions),
            large: true,
            required: true,
          }}
        />
      </Box>
      <Box mb={2}>
        <ControlledSelect
          control={control}
          name='documentConversionType'
          defaultValue={currentParserVersion?.document_conversion}
          SelectProps={{
            label: 'Document Conversion',
            options: getParserOptions(documentConversionOptions),
            large: true,
            required: true,
          }}
        />
      </Box>
    </ModalBase>
  )
}
